import { fromJS, toMap } from "immutable"
import {
    loadAlignmentTypesActionDict,
    loadAlignmentTypeActionDict,
    updateAlignmentTypeActionDict,
    loadProfileAlignmentActionDict,
    loadIssueAlignmentsActionDict,
    postProfileAlignmentActionDict,
    postIssueAlignmentActionDict,
    removeProfileAlignmentActionDict,
    removeIssueAlignmentActionDict
} from "shared/stakeholder/action-types"

const initialState = fromJS({
    alignmentTypes: [],
    alignmentType: {},
    profileAlignment: {
        isLoading: false,
    },
    issueAlignments: {
        issues: {}
    },
})

export const getInitialState = additionalArgs => initialState.merge(fromJS(additionalArgs))

const stakeholderReducer = (state = initialState, action) => {
    switch (action.type) {
        // Load all AlignmentTypes
        case loadAlignmentTypesActionDict.start:
            return state
        case loadAlignmentTypesActionDict.success:
            return state
                .setIn(["alignmentTypes"], fromJS(action.response.objects))
        case loadAlignmentTypesActionDict.fail:
            return state

        // Load a single AlignmentType
        case loadAlignmentTypeActionDict.start:
            return state
                .setIn(["alignmentType"], fromJS({}))
                .setIn(["alignmentType", "isLoading"], true)
        case loadAlignmentTypeActionDict.success:
            return state
                .setIn(["alignmentType"], fromJS(action.response))
                .setIn(["alignmentType", "isLoading"], false)
        case loadAlignmentTypeActionDict.fail:
            return state
                .setIn(["alignmentType"], fromJS({}))
                .setIn(["alignmentType", "isLoading"], false)

        // Update a single AlignmentType
        case updateAlignmentTypeActionDict.start:
            return state.setIn(["alignmentType", "isLoading"], true)
        case updateAlignmentTypeActionDict.success:
        case updateAlignmentTypeActionDict.fail:
            return state.setIn(["alignmentType", "isLoading"], false)

        // Profile Alignments
        case loadProfileAlignmentActionDict.start:
            return state
                .setIn(["profileAlignment", "isLoading"], true)
                // If the user manages to navigate to a new profile before the request resolves,
                // they may enter a weird state where selectIsProfileAlignmentUpdating is always true.
                .removeIn(["profileAlignment", "currentAlignmentAction"])

        case loadProfileAlignmentActionDict.success:
            return state
                .setIn(["profileAlignment", "alignmentAction"], (action.response.objects.length && fromJS(action.response.objects[0])) || fromJS({}))
                .setIn(["profileAlignment", "isLoading"], false)

        case loadProfileAlignmentActionDict.fail:
            return state
                .setIn(["profileAlignment", "isLoading"], false)

        case postProfileAlignmentActionDict.start:
            // Optimistically update the alignment while setting a save point
            return state
                .setIn(["profileAlignment", "currentAlignmentAction"], state.getIn(["profileAlignment", "alignmentAction"]))
                .setIn(
                    ["profileAlignment", "alignmentAction"],
                    fromJS(Object.assign(
                        state.getIn(["profileAlignment", "alignmentAction"], fromJS({})).toJS(),
                        {
                            alignment_type: DjangIO.app.userdata.stakeholder.models.AlignmentType.resourceUriFromId(action.alignmentTypeId),
                            _extra: {
                                username: Userdata.username,
                                fullname: Userdata.name,
                            },
                            effective_start_date: new Date().toISOString()
                        }
                    ))
                )

        case postProfileAlignmentActionDict.success:
            // Clean up our save point
            return state.removeIn(["profileAlignment", "currentAlignmentAction"])

        case postProfileAlignmentActionDict.fail:
            // Revert to the save point
            return state
                .setIn(["profileAlignment", "alignmentAction"], state.getIn(["profileAlignment", "currentAlignmentAction"], fromJS({})))
                .removeIn(["profileAlignment", "currentAlignmentAction"])

        case removeProfileAlignmentActionDict.start:
            // Optimistically remove Alignment and create save point
            return state
                .setIn(["profileAlignment", "currentAlignmentAction"], state.getIn(["profileAlignment", "alignmentAction"]))
                .setIn(["profileAlignment", "alignmentAction"], fromJS({}))

        case removeProfileAlignmentActionDict.success:
            // Clean up save point
            return state.removeIn(["profileAlignment", "currentAlignmentAction"])

        case removeProfileAlignmentActionDict.fail:
            // Revert to save point
            return state
                .setIn(["profileAlignment", "alignmentAction"], state.getIn(["profileAlignment", "currentAlignmentAction"]))
                .removeIn(["profileAlignment", "currentAlignmentAction"])

        // Issue Alignments
        case loadIssueAlignmentsActionDict.start:
            return state.setIn(["issueAlignments", "isLoading"], true)

        case loadIssueAlignmentsActionDict.success:
            return state
                .setIn(["issueAlignments", "issues"], fromJS(action.response))
                .setIn(["issueAlignments", "isLoading"], false)

        case loadIssueAlignmentsActionDict.fail:
            return state
                .setIn(["issueAlignments", "issues"], fromJS({}))
                .setIn(["issueAlignments", "isLoading"], false)

        case postIssueAlignmentActionDict.start:
            // Optimistically update the Alignment of this issue, but keep a record of the current value in case it fails
            return state
                .setIn(
                    ["issueAlignments", "currentAlignmentTypes"],
                    fromJS({
                        ...state.getIn(["issueAlignments", "currentAlignmentTypes"], fromJS({})).toJS(),
                        [action.issueId]: state.getIn(["issueAlignments", "issues", `${action.issueId}`], undefined)
                    })
                )
                .setIn(
                    ["issueAlignments", "issues"],
                    fromJS({
                        ...state.getIn(["issueAlignments", "issues"]).toJS(),
                        [action.issueId]: action.alignmentTypeId
                    })
                )

        case removeIssueAlignmentActionDict.start:
            // Optimistically remove the Alignment of this issue, but keep a record of the current value in case it fails
            // Make sure we're removing an Issue Alignment
            return state
                .setIn(
                    ["issueAlignments", "currentAlignmentTypes"],
                    fromJS({
                        ...state.getIn(["issueAlignments", "currentAlignmentTypes"], fromJS({})).toJS(),
                        [action.issueId]: state.getIn(["issueAlignments", "issues", `${action.issueId}`], undefined)
                    })
                )
                .removeIn(["issueAlignments", "issues", `${action.issueId}`])

        case removeIssueAlignmentActionDict.success:
        case postIssueAlignmentActionDict.success:
            // On success, clean up the old alignment_type
            return state.removeIn(["issueAlignments", "currentAlignmentTypes", `${action.issueId}`])

        case removeIssueAlignmentActionDict.fail:
        case postIssueAlignmentActionDict.fail:
            // On fail, revert to the old alignment_type and clean up
            return state
                .setIn(
                    ["issueAlignments", "issues"],
                    fromJS({
                        ...state.getIn(["issueAlignments", "issues"], fromJS({})).toJS(),
                        [action.issueId]: state.getIn(["issueAlignments", "currentAlignmentTypes", `${action.issueId}`])
                    })
                )
                .removeIn(["issueAlignments", "currentAlignmentTypes", `${action.issueId}`])

        default:
            return state
    }
}

export default stakeholderReducer
